import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import axios from 'axios';

const DnsForm = ({
    showModal,
    handleClose,
    domainId,
    domainName,
    zoneId,
    instanceIp,
    dnsRecordId,
    dnsRecordData,
    addDnsRecord,
    editDnsRecord,
}) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        domain_id: domainId,
        type: 'A',
        name: '',
        content: '',
        ttl: 3600,
        proxied: false,
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (showModal) {
            if (dnsRecordData) {
                setFormData({
                    domain_id: dnsRecordData.domain_id,
                    type: dnsRecordData.type || 'A',
                    name: dnsRecordData.name || '',
                    content: dnsRecordData.content || '',
                    ttl: dnsRecordData.ttl || 3600,
                    proxied: dnsRecordData.proxied === 1 || dnsRecordData.proxied === true,
                });
            } else {
                setFormData({
                    domain_id: domainId,
                    type: 'A',
                    name: '',
                    content: '',
                    ttl: 3600,
                    proxied: false,
                });
            }
        }
    }, [showModal, dnsRecordData, domainId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
        }
    };

    useEffect(() => {
        if (showModal && !dnsRecordId && !formData.content && instanceIp) {
            setFormData(prev => ({ ...prev, content: instanceIp }));
        }
    }, [instanceIp, showModal, dnsRecordId, formData.content]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.type) newErrors.type = 'Type is required';
        if (!formData.name) {
            newErrors.name = 'Name is required';
        } else if (!/^[a-zA-Z0-9.-]+$/.test(formData.name)) {
            newErrors.name = 'Invalid format. Use alphanumeric, dots, and hyphens only.';
        }
        if (!formData.content) newErrors.content = 'Content (IP/Target) is required';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return;
        }

        try {
            let apiErrors;
            if (dnsRecordId) {
                apiErrors = await editDnsRecord(dnsRecordId, formData);
            } else {
                apiErrors = await addDnsRecord(formData);
            }

            if (apiErrors && Object.keys(apiErrors).length > 0) {
                setErrors(apiErrors);
            } else {
                setErrors({});
                handleClose();
            }
        } catch (error) {
            console.error("An error occurred:", error);
        } finally {
            setLoading(false);
        }
    };

    const controlStyle = {
        fontSize: '14px',
        borderRadius: '14px',
        height: '39px',
        border: '0.5px solid rgb(232, 233, 237)'
    };

    return (
        <Offcanvas 
            show={showModal} 
            onHide={handleClose} 
            placement="end" 
            style={{ width: '490px' }} 
            backdrop="static"
            className="dns-record-offcanvas"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    {dnsRecordId ? 'Edit DNS Record' : 'Add DNS Record'}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <hr style={{ margin: 0 }} />

            <Offcanvas.Body>
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px' }}>Type:</Form.Label>
                        <Form.Control
                            type="text"
                            name="type"
                            placeholder="e.g. A, CNAME, TXT"
                            value={formData.type}
                            onChange={handleChange}
                            className={`form-control ${errors?.type ? 'is-invalid' : ''}`}
                            style={controlStyle}
                            required
                        />
                        {errors.type && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.type}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px' }}>Name (Subdomain):</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            placeholder="e.g. yourdomainapi.com or api"
                            value={formData.name}
                            onChange={handleChange}
                            className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                            style={controlStyle}
                            required
                        />
                        {errors.name && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.name}</div>}
                    </Form.Group>



                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px' }}>Content:</Form.Label>
                        <Form.Control
                            type="text"
                            name="content"
                            placeholder="e.g. 1.2.3.4"
                            value={formData.content}
                            onChange={handleChange}
                            className={`form-control ${errors?.content ? 'is-invalid' : ''}`}
                            style={controlStyle}
                            required
                        />
                        {errors.content && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.content}</div>}
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: '13px' }}>TTL:</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="ttl"
                                    value={formData.ttl}
                                    onChange={handleChange}
                                    style={controlStyle}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3 mt-4">
                                <Form.Check 
                                    type="switch"
                                    id="proxied-switch"
                                    label="Proxied"
                                    name="proxied"
                                    checked={formData.proxied}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="secondary" onClick={handleClose} className="me-2">
                            Close
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Save DNS Record'}
                        </Button>
                    </div>
                </Form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default DnsForm;
